import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import constant from "../../../utils/constant";
import Loader from "../../shared/loader/loader";
import SelectPageSize from "../../shared/common/pageSizeDropdown";
import Pagination from "../../shared/common/pagination";
import { getAllgrade } from "../../../api/grade.service";
import { getSubjectByGrade } from "../../../api/subject.service";
import Breadcrumb from "../../shared/common/breadcrumb";
import QuestionTypeSelect from "../../screen/questions/questionTypeSelect";
import Select from "react-select";
import Tooltip from "../../shared/common/ellipsisText";
import {
  FilterIcon,
  ResetIcon,
  EditIcon,
  AddStandardIcon,
  CaretDownSolidIcon,
  CaretUpSolidIcon,
  ApproveIcon,
  Disapprove,
  CopyIcon,
} from "../../../assets/icons/index";
import { ISelectElement } from "../../../model/interface/keyValuePair";
import {
  getVideoQuestions,
  postVideoQuestion,
} from "../../../api/videos.service";
import { getAuthours } from "../../../api/passage.service";
import { IQuestion } from "../../../model/interface/question";
import { IBreadcrumb } from "../../../model/interface/breadcrumb";
import { toast } from "react-toastify";
import { getAllDomainsBySubject } from "../../../api/domain.service";

const AssociateVideoQuestions: React.FC = () => {
  const defaultPageNumber = 1;
  const defaultPageSize = 10;
  const [loading, setLoading] = useState<boolean>(false);
  const [grade, setGrade] = useState<ISelectElement[]>([]);
  const [videoQuestions, setVideoQuestions] = useState<IQuestion[]>([]);
  const [subjects, setSubjects] = useState<ISelectElement[]>([]);
  const [domains, setDomains] = useState<ISelectElement[]>([]);
  const [authors, setAuthors] = useState<ISelectElement[]>([]);
  const [orderByColumn, setOrderByColumn] = useState("questionId");
  const [sortByDesc, setSortByAsc] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState(defaultPageNumber);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [totalPages, setTotalPages] = useState(0);
  const defaultValue = { label: "", value: "0" };
  const [selectedGrade, setSelectedGrade] = useState(defaultValue);
  const [selectedSubject, setSelectedSubject] = useState(defaultValue);
  const [selectedDomain, setSelectedDomain] = useState(defaultValue);
  const [selectedStandard, setSelectedStandard] = useState(defaultValue);
  const [isPublished, setIsPublished] = useState<boolean>(true);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [questionTypeId, setQuestionTypeId] = useState<number>(0);
  const [selectedAuthors, setSelectedAuthors] = useState(defaultValue);
  const [questionIds, setQuestionIds] = useState<string>("");
  const [topic, setTopic] = useState<string>("");
  const [standardId, setStandardId] = useState<number>(0);
  const [textKeywords, setTextKeywords] = useState<string>("");
  const [isDiagnosticQuestionChecked, setIsDiagnosticQuestionChecked] =
    useState<boolean>(false);
  const [isQuestionChecked, setIsQuestionChecked] = useState<boolean>(true);
  const location = useLocation();
  const parameters = location.state as any;
  const [videoId, setVideoId] = useState<number>(parameters?.associateVideoId);
  const videoRefIds: string[] = [
    parameters?.englishVideoRefId,
    parameters?.spanishVideoRefId,
  ];
  const navigate = useNavigate();
  const paginationRef: any = useRef(undefined);
  const pageSizeRef: any = useRef(undefined);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isDataFiltered, setIsDataFiltered] = useState<boolean>(false);

  const handleStandardId = (e) => {
    setStandardId(e.target.value);
  };

  const handleTextKeywords = (e) => {
    setTextKeywords(e.target.value);
  };

  const handleGradeChange = (selectedItem) => {
    setSelectedGrade(selectedItem);
    bindSubjects(selectedItem.value);
  };

  const handleSubjectChange = (selectedItem) => {
    setSelectedSubject(selectedItem);
    bindDomains(selectedItem.value);
  };

  const handleDomainChange = (selectedItem) => {
    setSelectedDomain(selectedItem);
  };

  const handleAuthourChange = (selectedItem) => {
    setSelectedAuthors(selectedItem);
  };

  const handleSortBy = (column: string) => {
    setSortByAsc(!sortByDesc);
    setOrderByColumn(column);
  };

  const handleEditQuestion = (questionId) => {
    navigate(constant.MANAGEQUESTION.replace(":id", questionId.toString()));
  };

  function handleQuestionTypeChange(selectedItem: any) {
    setQuestionTypeId(selectedItem.value);
  }

  const handleQuestionIdFilter = (e) => {
    setQuestionIds(e.target.value);

    if (e.target.value === "") {
      videoQuestion();
    }
  };

  const bindAPI = (column, type) => {
    setLoading(true);
    const passageData = {
      orderByColumn: column,
      sortByDesc: !sortByDesc ? "Asc" : "Desc",
      isPublished: isPublished,
      isDiagnostics:
        isDiagnosticQuestionChecked || type === "diagnostics" ? true : false,
      isActive: isActive,
      pageSize: pageSize,
      pageNumber: pageNumber,
    };

    getVideoQuestions(passageData).then((response) => {
      setVideoQuestions(response.data.data);
      setTotalPages(Math.ceil(response.data.totalRecords / pageSize));
      setTotalRecords(response.data.totalRecords);
      setLoading(false);
    });
  };

  const handleAddQuestion = async (
    questionId,
    englishQuestionRefId,
    spanishQuestionRefId
  ) => {
    const questionRefIds: string[] = [
      englishQuestionRefId,
      spanishQuestionRefId,
    ];
    setLoading(true);
    const questionToAdd = {
      questionId: questionId,
      videoId: videoId,
      userId: 0,
      questionRefIds: questionRefIds,
      videoRefIds: videoRefIds,
    };
    if (!questionToAdd.videoId) {
      toast.warning("Video id required!");
      setLoading(false);
    } else {
      try {
        await postVideoQuestion(questionToAdd);
        setLoading(false);
        toast.success("Video question added successfully");
        setTimeout(() => {
          navigate(
            videoId
              ? constant.VIDEOEDIT.replace(":id", videoId.toString())
              : constant.VIDEO
          );
        }, 1500);
      } catch (e) {
        toast.error("Something went wrong");
        setLoading(false);
        return;
      }
    }
  };

  function bindGrade() {
    setLoading(true);
    getAllgrade().then((response) => {
      setLoading(false);
      let newGrade = [];
      if (response.data.data.length > 0) {
        newGrade = response.data.data.map((element) => {
          return {
            value: element.gradeId || 0,
            label: `${element.name}`,
          };
        });
      }
      setGrade(newGrade);
    });
  }

  function bindSubjects(gradeId: number) {
    if (gradeId) {
      setLoading(true);
      getSubjectByGrade(999, 1, gradeId).then((response) => {
        setLoading(false);
        let newSubjects = [];
        if (response.data.data.length > 0) {
          newSubjects = response.data.data.map((element) => {
            return {
              value: element.subjectId || 0,
              label: `${element.name}`,
            };
          });
        }
        setSubjects(newSubjects);
      });
    }
  }

  function bindDomains(subjectId: number) {
    if (subjectId) {
      setLoading(true);
      getAllDomainsBySubject(subjectId).then((response) => {
        setLoading(false);
        let newSubjects = [];
        if (response.data.data.length > 0) {
          newSubjects = response.data.data.map((element) => {
            return {
              value: element.domainId || 0,
              label: `${element.name}`,
            };
          });
        }
        setDomains(newSubjects);
      });
    }
  }

  function bindAuthor() {
    setLoading(true);
    getAuthours().then((response) => {
      setLoading(false);
      let newAuthors = [];
      if (response.data.length > 0) {
        newAuthors = response.data.map((element) => {
          return {
            value: element.id || 0,
            label: `${element.name}`,
          };
        });
      }
      setAuthors(newAuthors);
    });
  }

  const breadcrumbItems = () => {
    const items: Array<IBreadcrumb> = [
      { name: "video", url: constant.VIDEOS },
      {
        name: "Add Associate Questions",
        url: "",
      },
    ];

    return items;
  };

  const handleQuestion = (type) => {
    setIsQuestionChecked(type === "" ? true : false);
    setIsDiagnosticQuestionChecked(type === "diagnostics" ? true : false);
    setVideoQuestions([]);
    bindAPI("questionId", type);
  };

  async function videoQuestion(onReset?) {
    const question = {
      questionIds: onReset ? "" : questionIds,
      orderByColumn: "QuestionId",
      sortByDesc: !sortByDesc ? "ASC" : "DESC",
      isPublished: true,
      isDiagnostics: isDiagnosticQuestionChecked,
      isActive: isActive,
      pageSize: onReset ? defaultPageSize : pageSize,
      pageNumber: pageNumber,
      subjectId: onReset ? 0 : selectedSubject?.value,
      gradeId: onReset ? 0 : selectedGrade?.value,
      domainId: onReset ? 0 : selectedDomain?.value,
      standardId: onReset ? 0 : standardId,
      questionTypeId: onReset ? 0 : questionTypeId,
      destinationVideoId: videoId,
    };
    setLoading(true);
    const questionResponse = getVideoQuestions(question);
    const questionData = (await questionResponse).data;
    setVideoQuestions(questionData.data);
    setTotalPages(Math.ceil(questionData.totalRecords / pageSize));
    setTotalRecords(questionData.totalRecords);
    setLoading(false);
  }

  async function handleFilter(e) {
    e.preventDefault();
    const question = {
      questionIds: questionIds,
      orderByColumn: "QuestionId",
      sortByDesc: !sortByDesc ? "ASC" : "DESC",
      isPublished: isPublished,
      isDiagnostics: isDiagnosticQuestionChecked,
      isActive: isActive,
      pageSize: pageSize,
      pageNumber: pageNumber,
      subjectId: selectedSubject?.value,
      gradeId: selectedGrade?.value,
      domainId: selectedDomain?.value,
      standardId: standardId || 0,
      questionTypeId: questionTypeId,
      destinationVideoId: videoId,
      createdByUserId: selectedAuthors?.value,
      questionText: textKeywords,
    };
    setLoading(true);
    const questionResponse = getVideoQuestions(question);
    const questionData = (await questionResponse).data;
    setVideoQuestions(questionData.data);
    setTotalPages(Math.ceil(questionData.totalRecords / pageSize));
    setTotalRecords(questionData.totalRecords);
    setLoading(false);
    setIsDataFiltered(true);
  }

  const handleReset = () => {
    setQuestionIds("");
    setTopic("");
    setStandardId(0);
    setTextKeywords("");
    setSelectedGrade(defaultValue);
    setSelectedSubject(defaultValue);
    setSelectedDomain(defaultValue);
    setSelectedStandard(defaultValue);
    setSelectedAuthors(defaultValue);
    setQuestionTypeId(0);
    setIsQuestionChecked(true);
    setIsDiagnosticQuestionChecked(false);
    videoQuestion(true);
    paginationRef?.current?.onReset();
    pageSizeRef?.current?.onReset();
  };
  function bindAssociatedStandardData() {
    if (parameters?.associateStandardData) {
      setSelectedStandard({
        label: parameters.associateStandardData.standardName,
        value: parameters.associateStandardData.standardId,
      });
      setStandardId(parameters.associateStandardData.standardId);
      setSelectedSubject({
        label: parameters.associateStandardData.subjectName,
        value: parameters.associateStandardData.subjectId,
      });
      setSelectedDomain({
        label: parameters.associateStandardData.domainName,
        value: parameters.associateStandardData.domainId,
      });
    }
  }
  useEffect(() => {
    bindGrade();
    bindAuthor();

    bindAssociatedStandardData();
  }, []);

  useEffect(() => {
    if (isLoaded) {
      if (isDataFiltered) {
        videoQuestion(false);
      } else {
        videoQuestion(true);
      }
    }
  }, [pageNumber]);

  useEffect(() => {
    if (pageNumber === 1) {
      videoQuestion();

      setIsLoaded(true);
    } else {
      paginationRef?.current?.onReset();
    }
  }, [pageSize]);

  useEffect(() => {
    if (isLoaded) {
      videoQuestion(isDataFiltered ? false : true);
    }
  }, [sortByDesc]);

  const copyQuestionIds = () => {
    if (questionIds) {
      setQuestionIds("");
    } else {
      let LS: any = localStorage.getItem("clipboard");
      if (LS) {
        LS = JSON.parse(LS);
        setQuestionIds(LS?.items);
      }
    }
  };

  return (
    <>
      {loading && <Loader />}

      <div className="w-full">
        <div className="flex-1">
          <div className=" h-auto min-h-half w-full rounded-bl-3xl bg-site-teal px-8  py-8 pt-0 pb-14 mt-7 ">
            <div className="relative  py-1">
              <div className="overlay-image-main pointer-events-none fixed left-0 top-0 w-screen h-screen z-20"></div>
              <div>
                <h3 className="text-lg font-light text-gray-900">
                  Add Associate Questions
                  <Breadcrumb items={breadcrumbItems()} />
                </h3>
              </div>

              <div>
                <fieldset className="mt-4">
                  <div className="space-y-4 sm:flex sm:items-center sm:space-y-0 sm:space-x-10">
                    <div className="flex items-center">
                      <input
                        id="question"
                        name="notification-method"
                        type="radio"
                        onClick={() => handleQuestion("")}
                        checked={isQuestionChecked}
                        className="focus:ring-primary-color h-4 w-4 text-primary-color border-gray-300"
                      />
                      <label className="ml-3 block text-sm font-medium text-gray-700">
                        Questions
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        id="digQuestion"
                        name="Digquestion"
                        checked={isDiagnosticQuestionChecked}
                        type="radio"
                        onClick={() => handleQuestion("diagnostics")}
                        className="focus:ring-primary-color h-4 w-4 text-primary-color border-gray-300"
                      />
                      <label className="ml-3 block text-sm font-medium text-gray-700">
                        Diagnostic Questions
                      </label>
                    </div>
                  </div>
                </fieldset>
              </div>
              <form action="#" onSubmit={handleFilter}>
                <div className="pt-6 border-b pb-6">
                  <div className="flex w-full space-x-6">
                    <div className="w-9/12">
                      <div className="grid grid-cols-9 gap-6">
                        <div className="col-span-3 sm:col-span-3">
                          <label className="text-sm text-gray-700">Grade</label>

                          <Select
                            className="mt-1"
                            options={grade}
                            onChange={handleGradeChange}
                            value={selectedGrade}
                            isSearchable={true}
                            theme={(theme) => ({
                              ...theme,
                              colors: {
                                ...theme.colors,
                                primary25: "#B3B3B3",
                                primary: "#041E42",
                              },
                            })}
                            styles={{
                              control: (base, state) => ({
                                ...base,
                                background:
                                  document.getElementsByClassName("dark")
                                    ?.length > 0
                                    ? "#1A1C23"
                                    : "white",
                                borderColor:
                                  document.getElementsByClassName("dark")
                                    ?.length > 0
                                    ? "#6B7280"
                                    : "#D1D5DB",
                                borderWidth: "1px",
                                "&:hover": {
                                  borderColor:
                                    state.isFocused &&
                                    document.getElementsByClassName("dark")
                                      ?.length > 0
                                      ? "#6b7280"
                                      : state.isFocused
                                      ? "#4c5563"
                                      : "",
                                },
                              }),
                              singleValue: (base) => ({
                                ...base,
                                color:
                                  document.getElementsByClassName("dark")
                                    ?.length > 0
                                    ? "white"
                                    : "#374051",
                              }),
                              input: (base) => ({
                                ...base,
                                color:
                                  document.getElementsByClassName("dark")
                                    ?.length > 0
                                    ? "white"
                                    : "",
                              }),
                            }}
                          ></Select>
                        </div>

                        <div className="col-span-3 sm:col-span-3">
                          <label className="text-sm text-gray-700">
                            Subject
                          </label>

                          <Select
                            className="mt-1"
                            options={subjects}
                            onChange={handleSubjectChange}
                            value={selectedSubject}
                            isSearchable={true}
                            theme={(theme) => ({
                              ...theme,
                              colors: {
                                ...theme.colors,
                                primary25: "#B3B3B3",
                                primary: "#041E42",
                              },
                            })}
                            styles={{
                              control: (base, state) => ({
                                ...base,
                                background:
                                  document.getElementsByClassName("dark")
                                    ?.length > 0
                                    ? "#1A1C23"
                                    : "white",
                                borderColor:
                                  document.getElementsByClassName("dark")
                                    ?.length > 0
                                    ? "#6B7280"
                                    : "#D1D5DB",
                                borderWidth: "1px",
                                "&:hover": {
                                  borderColor:
                                    state.isFocused &&
                                    document.getElementsByClassName("dark")
                                      ?.length > 0
                                      ? "#6b7280"
                                      : state.isFocused
                                      ? "#4c5563"
                                      : "",
                                },
                              }),
                              singleValue: (base) => ({
                                ...base,
                                color:
                                  document.getElementsByClassName("dark")
                                    ?.length > 0
                                    ? "white"
                                    : "#374051",
                              }),
                              input: (base) => ({
                                ...base,
                                color:
                                  document.getElementsByClassName("dark")
                                    ?.length > 0
                                    ? "white"
                                    : "",
                              }),
                            }}
                          ></Select>
                        </div>

                        <div className="col-span-3 sm:col-span-3">
                          <label className="text-sm text-gray-700">
                            Domain
                          </label>

                          <Select
                            className="mt-1"
                            options={domains}
                            onChange={handleDomainChange}
                            value={selectedDomain}
                            isSearchable={true}
                            theme={(theme) => ({
                              ...theme,
                              colors: {
                                ...theme.colors,
                                primary25: "#B3B3B3",
                                primary: "#041E42",
                              },
                            })}
                            styles={{
                              control: (base, state) => ({
                                ...base,
                                background:
                                  document.getElementsByClassName("dark")
                                    ?.length > 0
                                    ? "#1A1C23"
                                    : "white",
                                borderColor:
                                  document.getElementsByClassName("dark")
                                    ?.length > 0
                                    ? "#6B7280"
                                    : "#D1D5DB",
                                borderWidth: "1px",
                                "&:hover": {
                                  borderColor:
                                    state.isFocused &&
                                    document.getElementsByClassName("dark")
                                      ?.length > 0
                                      ? "#6b7280"
                                      : state.isFocused
                                      ? "#4c5563"
                                      : "",
                                },
                              }),
                              singleValue: (base) => ({
                                ...base,
                                color:
                                  document.getElementsByClassName("dark")
                                    ?.length > 0
                                    ? "white"
                                    : "#374051",
                              }),
                              input: (base) => ({
                                ...base,
                                color:
                                  document.getElementsByClassName("dark")
                                    ?.length > 0
                                    ? "white"
                                    : "",
                              }),
                            }}
                          ></Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-9 gap-6 mt-5">
                        <div className="col-span-3 sm:col-span-3">
                          <label className="text-sm text-gray-700">
                            Standard Id
                          </label>
                          <input
                            id="standardId"
                            name="standardId"
                            type="text"
                            className="w-full mt-1 border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                            value={standardId || ""}
                            onChange={handleStandardId}
                          />
                        </div>
                        <div className="col-span-3 sm:col-span-3">
                          <label className="text-sm text-gray-700">
                            Authors
                          </label>
                          <Select
                            className="mt-1"
                            options={authors}
                            onChange={handleAuthourChange}
                            value={selectedAuthors}
                            isSearchable={true}
                            theme={(theme) => ({
                              ...theme,
                              colors: {
                                ...theme.colors,
                                primary25: "#B3B3B3",
                                primary: "#041E42",
                              },
                            })}
                            styles={{
                              control: (base, state) => ({
                                ...base,
                                background:
                                  document.getElementsByClassName("dark")
                                    ?.length > 0
                                    ? "#1A1C23"
                                    : "white",
                                borderColor:
                                  document.getElementsByClassName("dark")
                                    ?.length > 0
                                    ? "#6B7280"
                                    : "#D1D5DB",
                                borderWidth: "1px",
                                "&:hover": {
                                  borderColor:
                                    state.isFocused &&
                                    document.getElementsByClassName("dark")
                                      ?.length > 0
                                      ? "#6b7280"
                                      : state.isFocused
                                      ? "#4c5563"
                                      : "",
                                },
                              }),
                              singleValue: (base) => ({
                                ...base,
                                color:
                                  document.getElementsByClassName("dark")
                                    ?.length > 0
                                    ? "white"
                                    : "#374051",
                              }),
                              input: (base) => ({
                                ...base,
                                color:
                                  document.getElementsByClassName("dark")
                                    ?.length > 0
                                    ? "white"
                                    : "",
                              }),
                            }}
                          ></Select>
                        </div>
                        <div className="col-span-3 sm:col-span-3">
                          <label className="text-sm text-gray-700">
                            Question Type
                          </label>

                          <QuestionTypeSelect
                            onChange={handleQuestionTypeChange}
                            value={questionTypeId}
                          ></QuestionTypeSelect>
                        </div>
                      </div>
                      <div className="grid grid-cols-9 gap-6 mt-5">
                        <div className="col-span-3 sm:col-span-3">
                          <label className="text-sm text-gray-700">
                            Text Keywords
                          </label>
                          <input
                            id="keywords"
                            name="keywords"
                            type="text"
                            className="block mt-1 w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                            value={textKeywords}
                            onChange={handleTextKeywords}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex w-3/12 items-start contentArea">
                      <div className="w-full h-full">
                        <label className="text-sm text-gray-700 flex">
                          Question Ids{" "}
                          <CopyIcon
                            onClick={() => copyQuestionIds()}
                            title="Paste from clipboard"
                          />
                        </label>
                        <textarea
                          className="resize block h-full w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                          value={questionIds}
                          onChange={handleQuestionIdFilter}
                        ></textarea>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <button
                      type="submit"
                      className="inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg mt-4"
                    >
                      <FilterIcon className="w-3"></FilterIcon>
                      <span>Filter</span>
                    </button>
                    <button
                      onClick={handleReset}
                      type="button"
                      className="mt-4 py-1.5 px-4 inline-flex ml-3 items-center text-sm rounded-lg text-gray-700 text-primary-color border border-gray-300 gap-2 bg-white hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color "
                    >
                      <ResetIcon className="w-3 h-3"></ResetIcon>
                      <span>Reset Filter</span>
                    </button>
                  </div>
                </div>
              </form>

              <div>
                <div>
                  <div className="relative  py-1">
                    <div id="dataContainer" className="mt-4">
                      <div>
                        <div>
                          <div>
                            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-primary-color">
                                  <tr>
                                    <th
                                      scope="col"
                                      className=" px-3 py-2  font-light text-sm text-left text-white dark:text-white tracking-wider"
                                    >
                                      <span className="flex gap-2">
                                        Id
                                        <span className="cursor-pointer">
                                          {sortByDesc &&
                                          orderByColumn === "questionId" ? (
                                            <CaretUpSolidIcon
                                              onClick={() =>
                                                handleSortBy("questionId")
                                              }
                                              className="w-5 h-5"
                                            ></CaretUpSolidIcon>
                                          ) : (
                                            <CaretDownSolidIcon
                                              onClick={() =>
                                                handleSortBy("questionId")
                                              }
                                              className="w-5 h-5"
                                            ></CaretDownSolidIcon>
                                          )}
                                        </span>
                                      </span>
                                    </th>
                                    <th
                                      scope="col"
                                      className=" px-3   py-2 font-light text-sm text-white dark:text-white tracking-wider"
                                    >
                                      <span className="flex gap-2">
                                        Questions
                                        <span className="cursor-pointer">
                                          {sortByDesc &&
                                          orderByColumn === "questionText" ? (
                                            <CaretUpSolidIcon
                                              onClick={() =>
                                                handleSortBy("questionText")
                                              }
                                              className="w-5 h-5"
                                            ></CaretUpSolidIcon>
                                          ) : (
                                            <CaretDownSolidIcon
                                              onClick={() =>
                                                handleSortBy("questionText")
                                              }
                                              className="w-5 h-5"
                                            ></CaretDownSolidIcon>
                                          )}
                                        </span>
                                      </span>
                                    </th>
                                    <th
                                      scope="col"
                                      className=" px-3  align-middle whitespace-nowrap   py-2 pl-6 font-light text-sm text-white dark:text-white tracking-wider"
                                    >
                                      Diagnostic Questions
                                    </th>

                                    <th
                                      scope="col"
                                      className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider"
                                    >
                                      Action
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {videoQuestions.map((question, index) => (
                                    <tr
                                      key={question.questionId}
                                      className={
                                        index % 2 === 0
                                          ? "bg-white"
                                          : "bg-gray-100"
                                      }
                                    >
                                      <td
                                        onClick={() =>
                                          handleEditQuestion(
                                            question.questionId
                                          )
                                        }
                                        className="cursor-pointer font-medium pl-6 text-indigo-600 hover:text-indigo-500 hover:underline"
                                      >
                                        {question?.questionId}
                                      </td>
                                      <td className="py-2  capitalize  text-sm   font-normal text-gray-500">
                                        <Tooltip
                                          text={question?.questionText}
                                        />
                                      </td>
                                      <td className=" py-2  capitalize flex justify-center   text-sm  pl-12 font-normal text-gray-500">
                                        {question.isDiagnostics ? (
                                          <ApproveIcon />
                                        ) : (
                                          <Disapprove />
                                        )}
                                      </td>

                                      <td className="px-3 py-2  text-sm text-center font-normal text-gray-700">
                                        <div className="flex gap-3 justify-center">
                                          <AddStandardIcon
                                            onClick={() =>
                                              handleAddQuestion(
                                                question.questionId,
                                                question.englishQuestionReferenceId,
                                                question.spanishQuestionReferenceId
                                              )
                                            }
                                          />
                                          <EditIcon
                                            onClick={() =>
                                              handleEditQuestion(
                                                question.questionId
                                              )
                                            }
                                            className="w-4 text-xs font-light cursor-pointer hover:text-red-500 transaction ease-in-out duration-300"
                                          />
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-between items-center">
                      {videoQuestions.length !== 0 ? (
                        <>
                          <Pagination
                            pages={totalPages}
                            setPageNumber={setPageNumber}
                            ref={paginationRef}
                          />
                          <span>
                            <span className="text-sm pr-4 ml-auto text-left-700">
                              Showing {pageNumber} to{" "}
                              {totalRecords < pageSize
                                ? totalRecords
                                : pageSize}{" "}
                              of {totalRecords} results
                            </span>

                            <SelectPageSize
                              setPageSize={setPageSize}
                              type={"second"}
                              ref={pageSizeRef}
                            />
                          </span>
                        </>
                      ) : (
                        <p className="p-2">No Records Found</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AssociateVideoQuestions;
